// frontend/openaiModal.jsx
import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Checkbox,
  Chip,
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from "@material-ui/core";
import { 
  Visibility, 
  VisibilityOff, 
  ExpandMore,
  Add,
  Delete,
  Info,
  Settings,
  Timer,
  Chat,
  Business,
  Person,
  LocalOffer,
  ViewColumn
} from "@material-ui/icons";
import { InputAdornment } from "@material-ui/core";
import api from "../../services/api";

// Lista de modelos OpenAI suportados
const openAiModels = [
  "gpt-3.5-turbo",
  "gpt-3.5-turbo-1106", 
  "gpt-3.5-turbo-16k",
  "gpt-4",
  "gpt-4-turbo",
  "gpt-4o",
  "gpt-4o-mini"
];

// Lista de vozes disponíveis para OpenAI
const availableVoices = [
  "texto",
  "pt-BR-FabioNeural",
  "pt-BR-FranciscaNeural",
  "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural",
  "pt-BR-DonatoNeural",
  "pt-BR-ElzaNeural",
  "pt-BR-GiovannaNeural",
  "pt-BR-HumbertoNeural",
  "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural",
  "pt-BR-LeticiaNeural",
  "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural",
  "pt-BR-ValerioNeural",
  "pt-BR-YaraNeural"
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  sectionTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  flowModeCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  temporarySettings: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px dashed ${theme.palette.primary.light}`
  },
  keywordChip: {
    margin: theme.spacing(0.5),
  },
  helpText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    }
  },
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column"
  }
}));

// Esquema de validação para OpenAI
const OpenAiSchema = Yup.object().shape({
  name: Yup.string()
    .min(5, "Muito curto!")
    .max(100, "Muito longo!")
    .required("Obrigatório"),
  prompt: Yup.string()
    .min(50, "Muito curto!")
    .required("Descreva o treinamento para Inteligência Artificial"),
  model: Yup.string()
    .oneOf(openAiModels, "Modelo inválido")
    .required("Informe o modelo"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(4000, "Máximo 4000 tokens")
    .required("Informe o número máximo de tokens"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(2, "Máximo 2")
    .required("Informe a temperatura"),
  apiKey: Yup.string().required("Informe a API Key"),
  maxMessages: Yup.number()
    .min(1, "Mínimo 1 mensagem")
    .max(100, "Máximo 100 mensagens")
    .required("Informe o número máximo de mensagens"),
  delay: Yup.number()
    .min(0, "Mínimo 0 segundos")
    .max(60, "Máximo 60 segundos")
    .default(7),
  voice: Yup.string().required("Informe o modo para Voz"),
  voiceKey: Yup.string().when("voice", {
    is: (voice) => voice !== "texto",
    then: Yup.string().required("Voice Key é obrigatória para síntese de voz"),
    otherwise: Yup.string().notRequired()
  }),
  voiceRegion: Yup.string().when("voice", {
    is: (voice) => voice !== "texto",
    then: Yup.string().required("Voice Region é obrigatória para síntese de voz"),
    otherwise: Yup.string().notRequired()
  }),
  flowMode: Yup.string()
    .oneOf(["permanent", "temporary"], "Modo de fluxo inválido")
    .required("Selecione o modo de fluxo"),
  maxInteractions: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      // 0 = ilimitado (desabilita o limite no backend)
      .min(0, "0 = ilimitado")
      .max(50, "Máximo 50 interações")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  completionTimeout: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      // 0 = sem limite (desabilita o timeout no backend)
      .min(0, "0 = sem limite")
      .max(60, "Máximo 60 minutos")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  continueKeywords: Yup.array().when("flowMode", {
    is: "temporary",
    then: Yup.array()
      .of(Yup.string().required("Palavra-chave não pode estar vazia"))
      .min(1, "Pelo menos uma palavra-chave é obrigatória no modo temporário"),
    otherwise: Yup.array()
  }),
  objective: Yup.string().when(["flowMode", "autoCompleteOnObjective"], {
    is: (flowMode, autoComplete) => flowMode === "temporary" && autoComplete,
    then: Yup.string().required("Objetivo é obrigatório quando auto completar está ativo"),
    otherwise: Yup.string()
  })
});

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    prompt: "",
    model: "gpt-4o",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    maxTokens: 1000,
    temperature: 0.7,
    apiKey: "",
    maxMessages: 10,
    queueId: 0,
    delay: 7,
    
    // Campos para controle de fluxo
    flowMode: "permanent",
    maxInteractions: 5,
    completionTimeout: 10,
    continueKeywords: ["continuar", "próximo", "avançar"],
    objective: "",
    autoCompleteOnObjective: false,
    departmentTransfers: [],
    userTransfers: [],
    tagsAllowedForAI: [],
    lanesAllowedForAI: []
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [queues, setQueues] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [lanesList, setLanesList] = useState([]);
  const [integration, setIntegration] = useState(initialState);
  const [labels, setLabels] = useState({
    title: "Adicionar OpenAI ao fluxo",
    btn: "Adicionar",
  });
  const [newKeyword, setNewKeyword] = useState("");

  useEffect(() => {
    if (open === "create" || open === "edit") {
      api.get("/queue").then(({ data }) => setQueues(Array.isArray(data) ? data : [])).catch(() => setQueues([]));
      api.get("/users", { params: { showAll: true } }).then(({ data }) => setUsersList(Array.isArray(data?.users) ? data.users : [])).catch(() => setUsersList([]));
      api.get("/tags/list").then(({ data }) => setTagsList(Array.isArray(data) ? data : [])).catch(() => setTagsList([]));
      api.get("/tag/kanban").then(({ data }) => setLanesList(data?.lista || [])).catch(() => setLanesList([]));
    }
  }, [open]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar OpenAI do fluxo",
        btn: "Salvar",
      });
      const typebotIntegration = data?.data?.typebotIntegration || {};
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        model: openAiModels.includes(typebotIntegration.model)
          ? typebotIntegration.model
          : "gpt-4o",
        flowMode: typebotIntegration.flowMode || "permanent",
        continueKeywords: typebotIntegration.continueKeywords || ["continuar", "próximo", "avançar"],
        // Preservar 0 (0 = sem limite) ao re-hidratar valores no modo edit
        maxInteractions: typebotIntegration.maxInteractions ?? 5,
        completionTimeout: typebotIntegration.completionTimeout ?? 10,
        delay: typeof typebotIntegration.delay === "number" ? typebotIntegration.delay : 7,
        objective: typebotIntegration.objective || "",
        autoCompleteOnObjective: typebotIntegration.autoCompleteOnObjective || false,
        departmentTransfers: Array.isArray(typebotIntegration.departmentTransfers)
          ? typebotIntegration.departmentTransfers.map((d) => ({
              queueId: Number(d.queueId) || 0,
              name: d.name || "",
              keywords: Array.isArray(d.keywords) ? d.keywords.join(", ") : (d.keywords || "")
            }))
          : [],
        userTransfers: Array.isArray(typebotIntegration.userTransfers)
          ? typebotIntegration.userTransfers.map((u) => ({
              userId: Number(u.userId) || 0,
              name: u.name || "",
              queueId: Number(u.queueId) || 0,
              keywords: Array.isArray(u.keywords) ? u.keywords.join(", ") : (u.keywords || "")
            }))
          : [],
        tagsAllowedForAI: Array.isArray(typebotIntegration.tagsAllowedForAI) ? typebotIntegration.tagsAllowedForAI : [],
        lanesAllowedForAI: Array.isArray(typebotIntegration.lanesAllowedForAI) ? typebotIntegration.lanesAllowedForAI : []
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar OpenAI ao fluxo",
        btn: "Adicionar",
      });
      setIntegration(initialState);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    setNewKeyword("");
    close(null);
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const departmentTransfers = (values.departmentTransfers || [])
      .filter((d) => d && Number(d.queueId) > 0 && d.name)
      .map((d) => ({
        queueId: Number(d.queueId),
        name: String(d.name).trim(),
        keywords: typeof d.keywords === "string"
          ? d.keywords.split(",").map((s) => s.trim()).filter(Boolean)
          : (Array.isArray(d.keywords) ? d.keywords : [])
      }));
    const userTransfers = (values.userTransfers || [])
      .filter((u) => u && Number(u.userId) > 0 && Number(u.queueId) > 0 && u.name)
      .map((u) => ({
        userId: Number(u.userId),
        name: String(u.name).trim(),
        queueId: Number(u.queueId),
        keywords: typeof u.keywords === "string"
          ? u.keywords.split(",").map((s) => s.trim()).filter(Boolean)
          : (Array.isArray(u.keywords) ? u.keywords : [])
      }));
    const tagsAllowedForAI = (values.tagsAllowedForAI || [])
      .filter((t) => t && Number(t.tagId) > 0 && t.name)
      .map((t) => ({ tagId: Number(t.tagId), name: String(t.name).trim() }));
    const lanesAllowedForAI = (values.lanesAllowedForAI || [])
      .filter((l) => l && Number(l.tagId) > 0 && l.name)
      .map((l) => ({ tagId: Number(l.tagId), name: String(l.name).trim() }));
    const promptData = {
      ...values,
      // Garantir que campos do modo temporário sejam nulos se modo for permanente
      maxInteractions: values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout: values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords: values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective: values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
      departmentTransfers,
      userTransfers,
      tagsAllowedForAI,
      lanesAllowedForAI,
      // Forçar provider como openai
      provider: "openai"
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { typebotIntegration: promptData },
      });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({
        typebotIntegration: promptData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const addKeyword = (arrayHelpers, keyword) => {
    if (keyword.trim() && !integration.continueKeywords.includes(keyword.trim())) {
      arrayHelpers.push(keyword.trim());
      setNewKeyword("");
    }
  };

  const removeKeyword = (arrayHelpers, index) => {
    arrayHelpers.remove(index);
  };

  const getModelDisplayName = (model) => {
    const modelNames = {
      "gpt-3.5-turbo": "GPT 3.5 Turbo",
      "gpt-3.5-turbo-1106": "GPT 3.5 Turbo (1106)",
      "gpt-3.5-turbo-16k": "GPT 3.5 Turbo 16K",
      "gpt-4": "GPT 4",
      "gpt-4-turbo": "GPT 4 Turbo",
      "gpt-4o": "GPT 4o",
      "gpt-4o-mini": "GPT 4o Mini"
    };
    return modelNames[model] || model;
  };

  const getVoiceDisplayName = (voice) => {
    if (voice === "texto") return "Apenas Texto";
    return voice.replace("pt-BR-", "").replace("Neural", "");
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {labels.title}
        </DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={OpenAiSchema}
          onSubmit={handleSavePrompt}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                
                {/* CONFIGURAÇÕES BÁSICAS */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Configurações Básicas - OpenAI
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <Field
                      as={TextField}
                      label="Nome do Assistente"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                    />

                    <FormControl fullWidth margin="dense" variant="outlined">
                      <Field
                        as={TextField}
                        label="API Key OpenAI"
                        name="apiKey"
                        type={showApiKey ? "text" : "password"}
                        error={touched.apiKey && Boolean(errors.apiKey)}
                        helperText={touched.apiKey && errors.apiKey}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                                {showApiKey ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>

                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.model && Boolean(errors.model)}
                    >
                      <InputLabel>Modelo OpenAI</InputLabel>
                      <Field
                        as={Select}
                        label="Modelo OpenAI"
                        name="model"
                      >
                        {openAiModels.map((model) => (
                          <MenuItem key={model} value={model}>
                            {getModelDisplayName(model)}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.model && errors.model && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {errors.model}
                        </div>
                      )}
                    </FormControl>

                    <Field
                      as={TextField}
                      label="Prompt do Sistema"
                      name="prompt"
                      error={touched.prompt && Boolean(errors.prompt)}
                      helperText={touched.prompt && errors.prompt}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      rows={6}
                      multiline
                      placeholder="Descreva como a IA deve se comportar, que informações deve coletar, como deve responder..."
                    />

                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Temperatura"
                        name="temperature"
                        error={touched.temperature && Boolean(errors.temperature)}
                        helperText={touched.temperature && errors.temperature || "0 = conservador, 2 = criativo"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                        inputProps={{
                          step: "0.1",
                          min: "0",
                          max: "2",
                        }}
                      />
                      <Field
                        as={TextField}
                        label="Max Tokens"
                        name="maxTokens"
                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                        helperText={touched.maxTokens && errors.maxTokens || "Tamanho máximo da resposta"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                      <Field
                        as={TextField}
                        label="Max Mensagens"
                        name="maxMessages"
                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                        helperText={touched.maxMessages && errors.maxMessages || "Histórico de contexto"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                      <Field
                        as={TextField}
                        label="Delay da IA (segundos)"
                        name="delay"
                        error={touched.delay && Boolean(errors.delay)}
                        helperText={touched.delay && errors.delay || "Aguarda este tempo antes de responder (agrupa sequência de mensagens do contato). Padrão: 7"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, max: 60 }}
                      />
                    </div>

                  </AccordionDetails>
                </Accordion>

                {/* CONFIGURAÇÕES DE VOZ */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Chat />
                      Configurações de Voz (Opcional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.voice && Boolean(errors.voice)}
                    >
                      <InputLabel>Tipo de Resposta</InputLabel>
                      <Field
                        as={Select}
                        label="Tipo de Resposta"
                        name="voice"
                      >
                        {availableVoices.map((voice) => (
                          <MenuItem key={voice} value={voice}>
                            {getVoiceDisplayName(voice)}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.voice && errors.voice && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {errors.voice}
                        </div>
                      )}
                    </FormControl>

                    {values.voice !== "texto" && (
                      <div className={classes.multFieldLine}>
                        <Field
                          as={TextField}
                          label="Voice Key (Azure)"
                          name="voiceKey"
                          error={touched.voiceKey && Boolean(errors.voiceKey)}
                          helperText={touched.voiceKey && errors.voiceKey}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          placeholder="Chave do Azure Speech Service"
                        />
                        <Field
                          as={TextField}
                          label="Voice Region (Azure)"
                          name="voiceRegion"
                          error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                          helperText={touched.voiceRegion && errors.voiceRegion}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          placeholder="ex: brazilsouth"
                        />
                      </div>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* COMPORTAMENTO DO FLUXO */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Timer />
                      Comportamento do Fluxo
                      <Tooltip title="Configure como o OpenAI deve se comportar no fluxo">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl component="fieldset" margin="normal">
                      <FormLabel component="legend">Modo de Funcionamento</FormLabel>
                      <RadioGroup
                        name="flowMode"
                        value={values.flowMode}
                        onChange={(e) => setFieldValue("flowMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="permanent"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Permanente</strong> - Encerrar fluxo aqui
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                O usuário fica conversando com a IA até pedir transferência ou encerrar
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="temporary"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Temporário</strong> - Volta ao fluxo depois
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA executa uma tarefa específica e depois retorna ao fluxo normal
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* CONFIGURAÇÕES DO MODO TEMPORÁRIO */}
                    {values.flowMode === "temporary" && (
                      <div className={classes.temporarySettings}>
                        <Typography variant="h6" gutterBottom>
                          ⏱️ Configurações do Modo Temporário
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Configure quando a IA deve parar e retornar ao fluxo
                        </Typography>

                        {/* Limite de Interações */}
                        <Field
                          as={TextField}
                          label="Máximo de Interações"
                          name="maxInteractions"
                          error={touched.maxInteractions && Boolean(errors.maxInteractions)}
                          helperText={touched.maxInteractions && errors.maxInteractions || "Número máximo de mensagens antes de voltar ao fluxo (0 = ilimitado)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 50 }}
                        />

                        {/* Timeout */}
                        <Field
                          as={TextField}
                          label="Timeout (minutos)"
                          name="completionTimeout"
                          error={touched.completionTimeout && Boolean(errors.completionTimeout)}
                          helperText={touched.completionTimeout && errors.completionTimeout || "Tempo limite antes de voltar ao fluxo (0 = sem limite)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 60 }}
                        />

                        {/* Palavras-chave de Continuação */}
                        <FormControl fullWidth margin="dense">
                          <Typography variant="subtitle2" gutterBottom>
                            Palavras-chave para Continuar Fluxo
                          </Typography>
                          <FieldArray name="continueKeywords">
                            {(arrayHelpers) => (
                              <div>
                                <Box display="flex" gap={1} alignItems="center" mb={1}>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Digite uma palavra-chave"
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === "Enter") {
                                        addKeyword(arrayHelpers, newKeyword);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => addKeyword(arrayHelpers, newKeyword)}
                                    disabled={!newKeyword.trim()}
                                  >
                                    Adicionar
                                  </Button>
                                </Box>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {values.continueKeywords.map((keyword, index) => (
                                    <Chip
                                      key={index}
                                      label={keyword}
                                      className={classes.keywordChip}
                                      onDelete={() => removeKeyword(arrayHelpers, index)}
                                      deleteIcon={<Delete />}
                                      variant="outlined"
                                      size="small"
                                    />
                                  ))}
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                  Quando o usuário enviar uma dessas palavras, o fluxo continuará automaticamente
                                </Typography>
                                {touched.continueKeywords && errors.continueKeywords && (
                                  <Typography variant="caption" color="error">
                                    {errors.continueKeywords}
                                  </Typography>
                                )}
                              </div>
                            )}
                          </FieldArray>
                        </FormControl>

                        {/* Objetivo */}
                        <Field
                          as={TextField}
                          label="Objetivo da IA"
                          name="objective"
                          error={touched.objective && Boolean(errors.objective)}
                          helperText={touched.objective && errors.objective || "Descreva o que a IA deve completar (ex: 'Coletar nome, email e telefone')"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Ex: Coletar dados do cliente, Qualificar interesse, Diagnosticar problema..."
                        />

                        {/* Auto Completar */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.autoCompleteOnObjective}
                              onChange={(e) => setFieldValue("autoCompleteOnObjective", e.target.checked)}
                              name="autoCompleteOnObjective"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                Auto completar quando atingir objetivo
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA analisará automaticamente se completou o objetivo e voltará ao fluxo
                              </Typography>
                            </Box>
                          }
                        />

                        {/* Fila de Transferência */}
                        <Field
                          as={TextField}
                          label="ID da Fila (para transferência)"
                          name="queueId"
                          error={touched.queueId && Boolean(errors.queueId)}
                          helperText="ID da fila para onde transferir se usuário pedir atendente (0 = não transferir)"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0 }}
                        />

                      </div>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* TRANSFERÊNCIA POR DEPARTAMENTO */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <Business />
                      Transferência por departamento
                      <Tooltip title="Quando o contato pedir para falar com um departamento (ex: Comercial, Suporte), a IA reconhece e transfere com confirmação">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Adicione os departamentos (filas) e palavras-chave para reconhecimento. Ex.: contato diz &quot;quero falar com o comercial&quot; → transferência para a fila Comercial + mensagem de confirmação.
                    </Typography>
                    <FieldArray name="departmentTransfers">
                      {(arrayHelpers) => (
                        <>
                          {(values.departmentTransfers || []).map((item, index) => (
                            <Box key={index} display="flex" flexWrap="wrap" alignItems="flex-start" gap={2} mb={2} p={1} border={1} borderColor="divider" borderRadius={1}>
                              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                <InputLabel>Fila (departamento)</InputLabel>
                                <Field
                                  as={Select}
                                  name={`departmentTransfers.${index}.queueId`}
                                  label="Fila (departamento)"
                                >
                                  <MenuItem value={0}>Selecione a fila</MenuItem>
                                  {queues.map((q) => (
                                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <Field
                                as={TextField}
                                name={`departmentTransfers.${index}.name`}
                                label="Nome do departamento"
                                placeholder="Ex: Comercial"
                                variant="outlined"
                                size="small"
                                style={{ minWidth: 160 }}
                              />
                              <Field
                                as={TextField}
                                name={`departmentTransfers.${index}.keywords`}
                                label="Palavras-chave (opcional)"
                                placeholder="comercial, vendas, vendas por favor"
                                variant="outlined"
                                size="small"
                                style={{ flex: 1, minWidth: 200 }}
                                helperText="Separadas por vírgula. Se vazio, usa o nome do departamento."
                              />
                              <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => arrayHelpers.push({ queueId: 0, name: "", keywords: "" })}
                          >
                            Adicionar departamento
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </AccordionDetails>
                </Accordion>

                {/* TRANSFERÊNCIA PARA USUÁRIO */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <Person />
                      Transferência para usuário (atendente)
                      <Tooltip title="Quando o contato pedir para falar com um atendente específico (ex: João), a IA reconhece se esse usuário está na lista, transfere para ele e para a fila dele">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Adicione os atendentes que podem ser solicitados pelo nome. Ex.: contato diz &quot;quero falar com o João&quot; → transferência para o usuário João e para a fila configurada (respeitando regras de fila).
                    </Typography>
                    <FieldArray name="userTransfers">
                      {(arrayHelpers) => (
                        <>
                          {(values.userTransfers || []).map((item, index) => (
                            <Box key={index} display="flex" flexWrap="wrap" alignItems="flex-start" gap={2} mb={2} p={1} border={1} borderColor="divider" borderRadius={1}>
                              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                <InputLabel>Usuário (atendente)</InputLabel>
                                <Field
                                  as={Select}
                                  name={`userTransfers.${index}.userId`}
                                  label="Usuário (atendente)"
                                  onChange={(e) => {
                                    const uid = Number(e.target.value);
                                    const user = usersList.find((u) => u.id === uid);
                                    if (user) {
                                      setFieldValue(`userTransfers.${index}.userId`, user.id);
                                      setFieldValue(`userTransfers.${index}.name`, user.name || "");
                                    }
                                  }}
                                >
                                  <MenuItem value={0}>Selecione o usuário</MenuItem>
                                  {usersList.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <Field
                                as={TextField}
                                name={`userTransfers.${index}.name`}
                                label="Nome (reconhecimento)"
                                placeholder="Ex: João"
                                variant="outlined"
                                size="small"
                                style={{ minWidth: 160 }}
                                helperText="Nome exibido ao contato; use o nome do usuário ou apelido"
                              />
                              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                <InputLabel>Fila do atendente</InputLabel>
                                <Field
                                  as={Select}
                                  name={`userTransfers.${index}.queueId`}
                                  label="Fila do atendente"
                                >
                                  <MenuItem value={0}>Selecione a fila</MenuItem>
                                  {queues.map((q) => (
                                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <Field
                                as={TextField}
                                name={`userTransfers.${index}.keywords`}
                                label="Palavras-chave (opcional)"
                                placeholder="joão, falar com joão"
                                variant="outlined"
                                size="small"
                                style={{ flex: 1, minWidth: 200 }}
                                helperText="Separadas por vírgula. Se vazio, usa o nome."
                              />
                              <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => arrayHelpers.push({ userId: 0, name: "", queueId: 0, keywords: "" })}
                          >
                            Adicionar usuário
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </AccordionDetails>
                </Accordion>

                {/* TAGS QUE A IA PODE ADICIONAR AO CONTATO */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <LocalOffer />
                      Tags que a IA pode adicionar ao contato
                      <Tooltip title="A IA pode incluir na resposta 'Ação: Adicionar tag: [nome]' para marcar o contato com uma tag. Configure aqui quais tags estão permitidas.">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Selecione as tags que a IA pode adicionar ao contato quando fizer sentido (ex.: &quot;Interessado em vendas&quot;, &quot;Qualificado&quot;). A IA usará o nome exato da tag na resposta.
                    </Typography>
                    <FieldArray name="tagsAllowedForAI">
                      {(arrayHelpers) => (
                        <>
                          {(values.tagsAllowedForAI || []).map((item, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={2} mb={1}>
                              <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                                <InputLabel>Tag</InputLabel>
                                <Field
                                  as={Select}
                                  name={`tagsAllowedForAI.${index}.tagId`}
                                  label="Tag"
                                  onChange={(e) => {
                                    const tag = tagsList.find((t) => t.id === Number(e.target.value));
                                    if (tag) {
                                      setFieldValue(`tagsAllowedForAI.${index}.tagId`, tag.id);
                                      setFieldValue(`tagsAllowedForAI.${index}.name`, tag.name);
                                    }
                                  }}
                                >
                                  <MenuItem value={0}>Selecione uma tag</MenuItem>
                                  {tagsList.map((tag) => (
                                    <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => arrayHelpers.push({ tagId: 0, name: "" })}
                          >
                            Adicionar tag permitida
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </AccordionDetails>
                </Accordion>

                {/* LANES DO KANBAN QUE A IA PODE MOVER O CARD */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                    <Typography className={classes.sectionTitle}>
                      <ViewColumn />
                      Mover card no Kanban (por conversa)
                      <Tooltip title="A IA pode incluir na resposta 'Ação: Mover para lane: [nome]' para mover o card do ticket para outra coluna do Kanban. Configure aqui quais lanes estão permitidas.">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Selecione as lanes do Kanban para onde a IA pode mover o card quando a conversa indicar (ex.: &quot;Em andamento&quot;, &quot;Concluído&quot;). A IA usará o nome exato da lane na resposta.
                    </Typography>
                    <FieldArray name="lanesAllowedForAI">
                      {(arrayHelpers) => (
                        <>
                          {(values.lanesAllowedForAI || []).map((item, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={2} mb={1}>
                              <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                                <InputLabel>Lane do Kanban</InputLabel>
                                <Field
                                  as={Select}
                                  name={`lanesAllowedForAI.${index}.tagId`}
                                  label="Lane do Kanban"
                                  onChange={(e) => {
                                    const lane = lanesList.find((l) => l.id === Number(e.target.value));
                                    if (lane) {
                                      setFieldValue(`lanesAllowedForAI.${index}.tagId`, lane.id);
                                      setFieldValue(`lanesAllowedForAI.${index}.name`, lane.name);
                                    }
                                  }}
                                >
                                  <MenuItem value={0}>Selecione uma lane</MenuItem>
                                  {lanesList.map((lane) => (
                                    <MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>
                                  ))}
                                </Field>
                              </FormControl>
                              <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => arrayHelpers.push({ tagId: 0, name: "" })}
                          >
                            Adicionar lane permitida
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </AccordionDetails>
                </Accordion>

              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderOpenAIModal;